/**
 * PackManager — Card pack management with rarity weight configuration
 */

import { useEffect, useState, useCallback, useMemo } from 'react';
import { useAdminStore } from '../../../store/adminStore';
import type { CardPack } from '../../../services/AdminAPI';

interface WeightEntry {
  rarity: string;
  weight: number;
}

const DEFAULT_WEIGHTS: WeightEntry[] = [
  { rarity: 'SSR', weight: 3 },
  { rarity: 'SR', weight: 12 },
  { rarity: 'R', weight: 35 },
  { rarity: 'N', weight: 50 },
];

export function PackManager() {
  const { packs, isLoading, error, loadPacks, createPack, updatePack, deletePack } = useAdminStore();
  const [editingPack, setEditingPack] = useState<{
    id?: string;
    name: string;
    description: string;
    price: number;
    cards_per_pack: number;
    weights: WeightEntry[];
  } | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    void loadPacks();
  }, [loadPacks]);

  const handleCreate = useCallback(() => {
    setIsCreating(true);
    setEditingPack({
      name: '',
      description: '',
      price: 100,
      cards_per_pack: 5,
      weights: [...DEFAULT_WEIGHTS],
    });
  }, []);

  const handleEdit = useCallback((pack: CardPack) => {
    setIsCreating(false);
    setEditingPack({
      id: pack.id,
      name: pack.name,
      description: pack.description,
      price: pack.price,
      cards_per_pack: pack.cards_per_pack,
      weights: pack.weights.map((w) => ({ rarity: w.rarity, weight: w.weight })),
    });
  }, []);

  const handleSave = useCallback(async () => {
    if (!editingPack) return;
    try {
      if (isCreating) {
        await createPack({
          name: editingPack.name,
          description: editingPack.description,
          price: editingPack.price,
          cards_per_pack: editingPack.cards_per_pack,
          weights: editingPack.weights,
        });
        setMessage('卡包已建立');
      } else if (editingPack.id) {
        await updatePack(editingPack.id, {
          name: editingPack.name,
          description: editingPack.description,
          price: editingPack.price,
          cards_per_pack: editingPack.cards_per_pack,
          weights: editingPack.weights.map((w) => ({
            id: '', pack_id: editingPack.id ?? '', rarity: w.rarity, weight: w.weight,
          })),
        });
        setMessage('卡包已更新');
      }
      setEditingPack(null);
      setIsCreating(false);
    } catch {
      setMessage('操作失敗');
    }
    setTimeout(() => { setMessage(null); }, 2000);
  }, [editingPack, isCreating, createPack, updatePack]);

  const handleDelete = useCallback(async (id: string) => {
    await deletePack(id);
    setMessage('卡包已停用');
    setTimeout(() => { setMessage(null); }, 2000);
  }, [deletePack]);

  const handleWeightChange = useCallback((index: number, weight: number) => {
    if (!editingPack) return;
    const newWeights = [...editingPack.weights];
    newWeights[index] = { ...newWeights[index], weight };
    setEditingPack({ ...editingPack, weights: newWeights });
  }, [editingPack]);

  return (
    <div>
      <div style={styles.header}>
        <h1 style={styles.title}>📦 卡包管理</h1>
        <div style={styles.actions}>
          {message && <span style={styles.message}>{message}</span>}
          <button style={{ ...styles.actionButton, backgroundColor: '#3a8a3a' }} onClick={handleCreate}>
            ＋ 新增卡包
          </button>
        </div>
      </div>

      {error && <div style={styles.error}>❌ {error}</div>}

      {/* Edit Modal */}
      {editingPack && (
        <div style={styles.modal}>
          <div style={styles.modalContent}>
            <h3 style={styles.modalTitle}>{isCreating ? '新增卡包' : '編輯卡包'}</h3>
            <div style={styles.form}>
              <label style={styles.label}>
                名稱
                <input style={styles.input} value={editingPack.name}
                  onChange={(e) => { setEditingPack({ ...editingPack, name: e.target.value }); }} />
              </label>
              <label style={styles.label}>
                描述
                <input style={styles.input} value={editingPack.description}
                  onChange={(e) => { setEditingPack({ ...editingPack, description: e.target.value }); }} />
              </label>
              <div style={styles.row}>
                <label style={styles.label}>
                  價格 (星靈幣)
                  <input style={styles.input} type="number" value={editingPack.price}
                    onChange={(e) => { setEditingPack({ ...editingPack, price: parseInt(e.target.value) || 0 }); }} />
                </label>
                <label style={styles.label}>
                  每包張數
                  <input style={styles.input} type="number" value={editingPack.cards_per_pack}
                    onChange={(e) => { setEditingPack({ ...editingPack, cards_per_pack: parseInt(e.target.value) || 5 }); }} />
                </label>
              </div>

              {/* Weight Sliders */}
              <h4 style={styles.subTitle}>稀有度權重</h4>
              {editingPack.weights.map((w, i) => (
                <WeightSlider
                  key={w.rarity}
                  rarity={w.rarity}
                  weight={w.weight}
                  onChange={(val) => { handleWeightChange(i, val); }}
                />
              ))}

              {/* Probability Preview */}
              <ProbabilityPreview weights={editingPack.weights} />
            </div>
            <div style={styles.modalActions}>
              <button style={{ ...styles.actionButton, backgroundColor: '#3a8a3a' }} onClick={() => { void handleSave(); }}>
                儲存
              </button>
              <button style={styles.cancelButton} onClick={() => { setEditingPack(null); setIsCreating(false); }}>
                取消
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pack List */}
      {isLoading && packs.length === 0 && (
        <div style={styles.loading}>載入中...</div>
      )}

      <div style={styles.packGrid}>
        {packs.map((pack) => (
          <div key={pack.id} style={{ ...styles.packCard, opacity: pack.is_active ? 1 : 0.5 }}>
            <div style={styles.packHeader}>
              <h3 style={styles.packName}>{pack.name}</h3>
              <span style={styles.packPrice}>💰 {pack.price} 星靈幣</span>
            </div>
            <p style={styles.packDesc}>{pack.description || '（無描述）'}</p>
            <div style={styles.packInfo}>
              <span>每包 {pack.cards_per_pack} 張</span>
              <span>狀態: {pack.is_active ? '✅ 上架' : '❌ 下架'}</span>
            </div>
            {pack.weights.length > 0 && (
              <div style={styles.weightBars}>
                {pack.weights.map((w) => {
                  const totalWeight = pack.weights.reduce((sum, pw) => sum + pw.weight, 0);
                  const pct = totalWeight > 0 ? (w.weight / totalWeight * 100).toFixed(1) : '0';
                  return (
                    <div key={w.rarity} style={styles.weightBar}>
                      <span style={{ color: rarityColor(w.rarity), fontWeight: 'bold', width: '40px' }}>{w.rarity}</span>
                      <div style={styles.barBg}>
                        <div style={{ ...styles.barFill, width: `${pct}%`, backgroundColor: rarityColor(w.rarity) }} />
                      </div>
                      <span style={{ fontSize: '12px', width: '50px', textAlign: 'right' }}>{pct}%</span>
                    </div>
                  );
                })}
              </div>
            )}
            <div style={styles.packActions}>
              <button style={styles.smallButton} onClick={() => { handleEdit(pack); }}>✏️ 編輯</button>
              <button style={{ ...styles.smallButton, color: '#ff4444' }} onClick={() => { void handleDelete(pack.id); }}>🗑️ 停用</button>
            </div>
          </div>
        ))}
        {packs.length === 0 && !isLoading && (
          <div style={styles.loading}>暫無卡包</div>
        )}
      </div>
    </div>
  );
}

function WeightSlider({ rarity, weight, onChange }: { rarity: string; weight: number; onChange: (v: number) => void }) {
  return (
    <div style={styles.slider}>
      <span style={{ color: rarityColor(rarity), fontWeight: 'bold', width: '50px' }}>{rarity}</span>
      <input
        type="range" min={0} max={100} value={weight}
        style={{ flex: 1 }}
        onChange={(e) => { onChange(parseInt(e.target.value)); }}
      />
      <input
        type="number" min={0} max={100} value={weight}
        style={{ ...styles.input, width: '60px', textAlign: 'center' }}
        onChange={(e) => { onChange(parseInt(e.target.value) || 0); }}
      />
    </div>
  );
}

function ProbabilityPreview({ weights }: { weights: WeightEntry[] }) {
  const total = useMemo(() => weights.reduce((sum, w) => sum + w.weight, 0), [weights]);

  return (
    <div style={styles.preview}>
      <h5 style={styles.previewTitle}>機率預覽</h5>
      <div style={styles.previewBars}>
        {weights.map((w) => {
          const pct = total > 0 ? (w.weight / total * 100).toFixed(1) : '0.0';
          return (
            <div key={w.rarity} style={styles.previewBar}>
              <span style={{ color: rarityColor(w.rarity) }}>{w.rarity}</span>
              <div style={styles.barBg}>
                <div style={{ ...styles.barFill, width: `${pct}%`, backgroundColor: rarityColor(w.rarity) }} />
              </div>
              <span>{pct}%</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function rarityColor(rarity: string): string {
  switch (rarity) {
    case 'SSR': return '#ff8800';
    case 'SR': return '#cc44ff';
    case 'R': return '#4488ff';
    case 'N': return '#888888';
    default: return '#666666';
  }
}

const styles: Record<string, React.CSSProperties> = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' },
  title: { fontSize: '24px', margin: 0 },
  actions: { display: 'flex', gap: '8px', alignItems: 'center' },
  actionButton: {
    padding: '8px 14px', fontSize: '13px', backgroundColor: '#3a5a8a', color: '#ffffff',
    border: 'none', borderRadius: '4px', cursor: 'pointer',
  },
  cancelButton: {
    padding: '8px 14px', fontSize: '13px', backgroundColor: '#5a3a3a', color: '#ffffff',
    border: 'none', borderRadius: '4px', cursor: 'pointer',
  },
  message: { color: '#44ff44', fontSize: '13px' },
  error: { color: '#ff4444', padding: '8px', marginBottom: '8px' },
  loading: { padding: '40px', textAlign: 'center', color: '#888888' },
  packGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))', gap: '16px' },
  packCard: {
    backgroundColor: '#252545', borderRadius: '8px', padding: '20px',
    border: '1px solid #3a3a5a',
  },
  packHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' },
  packName: { fontSize: '18px', margin: 0 },
  packPrice: { fontSize: '14px', color: '#ffcc00' },
  packDesc: { fontSize: '13px', color: '#888888', margin: '0 0 12px 0' },
  packInfo: { display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#888888', marginBottom: '12px' },
  weightBars: { display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '12px' },
  weightBar: { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px' },
  barBg: { flex: 1, height: '8px', backgroundColor: '#1a1a2e', borderRadius: '4px', overflow: 'hidden' },
  barFill: { height: '100%', borderRadius: '4px', transition: 'width 0.3s' },
  packActions: { display: 'flex', gap: '8px', justifyContent: 'flex-end' },
  smallButton: {
    padding: '6px 12px', fontSize: '12px', backgroundColor: '#3a3a5a', color: '#ffffff',
    border: 'none', borderRadius: '4px', cursor: 'pointer',
  },
  modal: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', justifyContent: 'center', alignItems: 'center',
    zIndex: 1000,
  },
  modalContent: {
    backgroundColor: '#252545', borderRadius: '12px', padding: '24px',
    width: '500px', maxHeight: '80vh', overflow: 'auto',
  },
  modalTitle: { fontSize: '18px', margin: '0 0 16px 0' },
  modalActions: { display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '16px' },
  form: { display: 'flex', flexDirection: 'column', gap: '12px' },
  row: { display: 'flex', gap: '12px' },
  label: { display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '13px', color: '#cccccc', flex: 1 },
  input: {
    padding: '8px 12px', fontSize: '14px', backgroundColor: '#2a2a4a', color: '#ffffff',
    border: '1px solid #3a3a5a', borderRadius: '4px', outline: 'none',
  },
  subTitle: { fontSize: '14px', margin: '8px 0 4px 0', color: '#cccccc' },
  slider: { display: 'flex', alignItems: 'center', gap: '8px' },
  preview: { marginTop: '8px', padding: '12px', backgroundColor: '#1e1e3e', borderRadius: '6px' },
  previewTitle: { fontSize: '13px', margin: '0 0 8px 0', color: '#888888' },
  previewBars: { display: 'flex', flexDirection: 'column', gap: '4px' },
  previewBar: { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px' },
};
